<template>
  <!-- Ни вкладок, ни фильтра — шапка не нужна -->
  <nav
    v-if="roleConfig.showTabs === true || roleConfig.showFilter === true"
    id="transport-orders-page-header"
  >
    <VTabsV2
      v-if="roleConfig.showTabs"
      :tabs="tabs"
      :base-url="tabsBaseUrl"
    />

    <!--
      key по роли: при смене роли компонент фильтра пересоздаётся и собирает
      черновик из нового фильтра в data(). Без ключа он переиспользуется
      (один роут-рекорд у всех ролей) и фильтры остаются от прошлой роли.
    -->
    <TransportOrdersRegistryFilterComponent
      v-if="roleConfig.showFilter"
      :key="role"
      :filters="filter"
      @updated="onFilterUpdate"
    />
  </nav>

  <TransportOrdersRegistry
    :params="registryParams"
    :hidden-columns="roleConfig.hiddenColumns"
    :create-buttons="roleConfig.createButtons"
  />
</template>

<script lang="ts">

import TransportOrdersRegistryFilter          from '@/domains/TransportOrders/components/TransportOrdersRegistry/TransportOrdersRegistryFilter';
import TransportOrdersService                 from '@/domains/TransportOrders/TransportOrdersService';
import TransportOrder                         from '@/domains/TransportOrders/TransportOrder';
import TabElement                             from '@/lib/components/VTabsV2/TabElement';

import TransportOrdersRegistry                from '@/domains/TransportOrders/components/TransportOrdersRegistry/TransportOrdersRegistry.vue';
import TransportOrdersRegistryFilterComponent from '@/domains/TransportOrders/components/TransportOrdersRegistry/TransportOrdersRegistryFilter.vue';

import type { TransportOrderCreateButtonType } from '@/domains/TransportOrders/components/TransportOrderCreateButtons.vue';

/** База пути страницы. Роль дописывается сегментом, статус — следующим за ней. */
const BASE_URL = '/flow/transport-orders';

type Role = 'electrostal' | 'lsz' | 'general' | 'namangan';

type RoleConfig = {
  preset:        Partial<TransportOrdersRegistryFilter> | null;
  showTabs:      boolean,
  /** Показывать ли фильтр в шапке. Пресет роли применяется и без него. */
  showFilter:    boolean,
  /** Типы ЗТК, которые можно создать на этом экране. Пустой список — кнопок нет. */
  createButtons: TransportOrderCreateButtonType[],
  hiddenColumns: string[],
};

const ROLE_CONFIG: Record<Role, RoleConfig> = {
  electrostal: {
    preset:        { type: { id: 'consolidated',  name: 'Консолидированная' } },
    showTabs:      false,
    showFilter:    true,
    createButtons: [TransportOrder.Type.Consolidated],
    hiddenColumns: [
      'sender', 'receiver', 'type', 'status',
      'deadline', 'borderStatus', 'comment',
    ],
  },

  lsz: {
    preset:        { type: { id: 'single_sender', name: 'Обычная' } },
    showTabs:      true,
    showFilter:    true,
    createButtons: [],
    hiddenColumns: [
      'receiver', 'type', 'status',
      'cargoWeight','cargoUnits', 'cargoVolume', 
      'deadline', 'borderStatus', 'comment',
    ],
  },

  namangan: {
    preset:        { type: { id: 'internal', name: 'Внутреннее перемещение' } },
    showTabs:      false,
    showFilter:    false,
    createButtons: [
      TransportOrder.Type.OrderPositionPickup,
      TransportOrder.Type.Return,
      TransportOrder.Type.Service,
      TransportOrder.Type.Internal,
    ],
    hiddenColumns: [
      'sender', 'receiver',
      'cargoWeight', 'cargoUnits', 'cargoVolume',
      'deadline', 'borderStatus', 'comment',
    ],
  },

  general: {
    preset:        null,
    showTabs:      false,
    showFilter:    true,
    createButtons: [],
    hiddenColumns: ['status']
  },
};

function resolveRole(routeRole: unknown): Role {
  if (routeRole !== null && routeRole !== undefined) return routeRole;
  return 'general';
}

/**
 * Вкладки страницы. Собираются из TransportOrdersService.TABS — там же лежат
 * флаги запроса (isInWork/isConfirmed/isFinished), которыми пользуется
 * getRequestParamsByTab и фильтр колонки «Статус» в реестре.
 * Счётчиков по вкладкам бэк не отдаёт, count везде null.
 * У роли без вкладок — null, вкладки не собираются вовсе.
 */
function buildTabs(role: Role): TabElement[] | null {
  if (ROLE_CONFIG[role].showTabs === true) {
    const result: TabElement[] = [];

    for (const tab of TransportOrdersService.TABS) {
      result.push(TabElement.from(tab.id, tab.name));
    }

    return result;
  }
  else {
    return null;
  }
}

/**
 * Фильтр под роль: пользовательские значения из localStorage плюс пресет роли
 * поверх них. Функция модульного уровня, а не метод: нужна уже в data(),
 * где методы компонента ещё не гарантированы, и в this не нуждается.
 *
 * У роли без фильтра localStorage не читаем: пользователь не видит фильтр и не
 * сбросит сохранённые там значения. Остаётся только пресет роли.
 */
function buildFilter(role: Role): TransportOrdersRegistryFilter {
  const preset = ROLE_CONFIG[role].preset;

  let filter: TransportOrdersRegistryFilter;

  if (ROLE_CONFIG[role].showFilter === true) {
    filter = TransportOrdersRegistryFilter.fromLocalStorage();
  }
  else {
    filter = new TransportOrdersRegistryFilter();
  }

  if (preset !== null) {
    filter.applyPreset(preset);
  }

  return filter;
}

export default defineComponent({
  name: 'TransportOrdersPage',

  components: {
    TransportOrdersRegistry,
    TransportOrdersRegistryFilterComponent,
  },

  data() {
    return {
      filter: buildFilter(resolveRole(this.$route.params.role)),
      tabs: buildTabs(resolveRole(this.$route.params.role)),
    };
  },

  watch: {
    /**
     * Роль меняется без пересоздания компонента: у путей разных ролей один
     * роут-рекорд, отличается только параметр, поэтому data() второй раз не
     * выполняется и в фильтре остаётся пресет предыдущей роли.
     */
    role(newRole: Role): void {
      this.filter = buildFilter(newRole);
      this.tabs   = buildTabs(newRole);
    },
  },

  computed: {
    role(): Role {
      return resolveRole(this.$route.params.role);
    },

    roleConfig(): RoleConfig {
      return ROLE_CONFIG[this.role];
    },

    /**
     * Активная вкладка. Читается из пути, а не из @update VTabsV2: у роли без
     * вкладок компонента на странице нет, а статус всё равно нужен.
     * Сегмент пути опционален — когда его нет, работает первая вкладка.
     * null — у роли вкладок нет.
     */
    activeTab(): TabElement | null {
      if (this.tabs === null) {
        return null;
      }
      else {
        const tab = this.tabs.find(_tab => _tab.id === this.$route.params.status);

        if (tab === undefined) {
          return this.tabs[0];
        }
        else {
          return tab;
        }
      }
    },

    /**
     * baseUrl для VTabsV2. Роль — сегмент перед статусом, поэтому база у каждой
     * роли своя; у general роли в пути нет.
     */
    tabsBaseUrl(): string {
      if (this.role === 'general') {
        return BASE_URL;
      }
      else {
        return `${BASE_URL}/${this.role}`;
      }
    },

    /** Параметры реестра: из фильтра, если он есть у роли, и из вкладки, если есть вкладки */
    registryParams(): Parameters<typeof TransportOrdersService.getListPaginated>[0] {
      const result: Parameters<typeof TransportOrdersService.getListPaginated>[0] = {};

      if (this.roleConfig.showFilter === true) {
        Object.assign(result, this.filter.getRequestParams());
      }
      else {
        // фильтра у роли нет
      }

      if (this.activeTab !== null) {
        Object.assign(result, TransportOrdersService.getRequestParamsByTab(this.activeTab));
      }
      else {
        // вкладок у роли нет — статусом не ограничиваем
      }

      return result;
    },
  },

  methods: {
    onFilterUpdate(newFilter: TransportOrdersRegistryFilter): void {
      this.filter = newFilter;
    },
  },
});
</script>